import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {fromEvent, Subject, Subscription} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {SocketService} from '../../services/socket.service';
import {DownloadService} from '../../services/download.service';
import {A0, SnapshotType} from '../entities/A0.entity';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MapperService} from '../../services/mapper.service';
import {LocalParamsService} from '../../services/local-params.service';
import {LayerDatasource, LayerImagery, LayerLoadType} from './types';
import {LayerService} from '../../services/layer.service';
import {CesiumEventService} from '../../services/cesium-event.service';
import {BACKGROUND_DEFAULT, BACKGROUND_PROVIDERS, getDefaultBackgroundAliasForZone} from './custom-map-conf';
import {WatercourseLayer} from './layers/FRA/watercourse.layer';
import {WatercourseCIVLayer} from './layers/CIV/watercourse_civ.layer';
import {getCamera, getCameraBoundingBox, normalizeBBoxToDegrees} from './cesiumTools';
import {Camera, Viewer} from 'cesium';
import {getVisiblesPlanMarkersFromCurrentViewbox} from './planTools';
import {PlanLayer} from './layers/default/picto.layer';
import { getIconSize, INITIAL_PRINT_POSITION } from '../tools';

@Component({
    selector: 'app-map-cesium',
    template: `
    <mat-progress-bar
      *ngIf="upload"
      [mode]="upload.state == 'PENDING' ? 'buffer' : 'determinate'"
      [value]="upload.progress"
    ></mat-progress-bar>

    <svg class="d-none" id="labelSvg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256">
    </svg>


    <div class="d-flex justify-content-center align-items-center">
      <div class="d-block position-relative dragBoundary" #dragBoundary>
        <div class="map-container" #mapCesium ></div>

        <div #northArrow class="map-north-arrow map-north-arrow-{{canvasMode}}">
          <img src="../../assets/images/north-arrow.png" alt="Fleche nord">
        </div>

        <div class="map-echelle map-echelle-{{canvasMode}}">
          <span #echelleBar class="map-echelle_bar"></span>
          <span #echelleLabel class="map-echelle_label"></span>
        </div>

        <div *ngIf="isBuildingScreenShot | async" class="cesium-screenshot-loader">
          <div class="d-flex flex-column align-items-center">
            <mat-spinner [diameter]="50"></mat-spinner>
            <span class="bg-white p-2 mt-3 rounded-1" *ngIf="uploadLoadingMsg">{{uploadLoadingMsg}}</span>
          </div>
        </div>

        <ng-content></ng-content>
      </div>
      <canvas #canvasSnapshot width="1500" height="1500" class="canvas-print"></canvas>
    </div>
  `,
    styles: [
        `.cesium-viewer-toolbar {display: none}`
    ],
    standalone: false
})
export class MapCesiumComponent implements OnInit, AfterViewInit, OnDestroy {

  constructor(
    private socketService: SocketService,
    private downloadService: DownloadService,
    private mapperService: MapperService,
    private localParams: LocalParamsService,
    private snackbar: MatSnackBar,
    private layerService: LayerService,
    private cesiumEventService: CesiumEventService,
    private ngZone: NgZone,
  ) {
    this.$socket = this.socketService.getMap2DEventEmitter().subscribe((res) => {
      if (!res) {
        return;
      }


      if (typeof res.data === 'number') {
        this.uploadLoadingMsg = res.msg;
        this.upload = {
          progress: res.data,
          state: 'IN_PROGRESS'
        };
      }

      if (typeof res.data === 'string') {
        this.upload = null;

        this.feedback('danger', 'Une erreur est survenue')
      }

      if (!!res.done) {
        this.uploadLoadingMsg = '';
        this.feedback('success', 'le fichier 2d a été généré')
        this.upload = null;

        const screenshotOptions = {
          type: SnapshotType.MAIN,
          snapshot_url_image: res.done.url_hd,
          snapshot_url_image_lite: res.done.url_lite,
          pictos: this.visiblePictosOnMap.map(a => Number(a)),
          position: '0,0',
          bbox: JSON.stringify(getCameraBoundingBox(this.viewer))
        };

        this.mapperService.createOrUpdateSnapshot(this.plan, screenshotOptions).subscribe(res => {
          this.feedback('success', 'le fichier 2d a été généré');
          this.screenshotEvent.emit({type: 'success', data: screenshotOptions});
          this.isBuildingScreenShot.next(false);
        }, (err) => {
          this.feedback('danger', 'Une erreur est survenue');
          this.isBuildingScreenShot.next(false);
          this.screenshotEvent.emit({type: 'fail', data: screenshotOptions});
        });
      }
    });
  }

  @ViewChild('mapCesium') elementRef: ElementRef;

  @Input() change: EventEmitter<any> = new EventEmitter();
  @Input() id = 'cesiumMap';
  @Input() plan: A0;
  @Input() canvasMode = '2d';
  @Input() type;
  @Input() snapshot;

  @Input() typeRisque = 'inondation';

  @Output() screenshotEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() isLoadingLayer: Subject<boolean> = new Subject<boolean>();

  @ViewChild('northArrow') northArrow: ElementRef;
  @ViewChild('echelleLabel') echelleLabel;
  @ViewChild('echelleBar') echelleBar;
  @ViewChild('canvasSnapshot') canvasSnapshot;

  public viewer: any;
  public camera: Camera;
  public imageryLayers;
  public tilesToLoad;
  public uploadLoadingMsg = '';

  public visiblePictosOnMap = [];

  public background = BACKGROUND_DEFAULT;

  public iconSize = 20;

  upload;

  canvasWidth;
  canvasHeight;

  $socket: Subscription;

  dragging = false;

  echelle = {
    barWidth: null,
    distanceLabel: null
  };

  geodesic = new Cesium.EllipsoidGeodesic();

  distances = [
    1, 2, 3, 5,
    10, 20, 30, 50,
    100, 200, 300, 500,
    1000, 2000, 3000, 5000,
    10000, 20000, 30000, 50000,
    100000, 200000, 300000, 500000,
    1000000, 2000000, 3000000, 5000000,
    10000000, 20000000, 30000000, 50000000];

  @Output() whenMapIsInit: Subject<boolean> = new Subject();
  @Output() isMapLoadingTiles: Subject<boolean> = new Subject();
  @Output() isBuildingScreenShot: Subject<boolean> = new Subject();

  private destroyed = false;

  private buildingScreenshot = false;
  private resizeSub?: Subscription;
  private buildingScreenshotSub?: Subscription;

  ngOnDestroy(): void {
    this.destroyed = true;

    this.$socket.unsubscribe();
    this.cameraMoveEndSub?.unsubscribe();
    this.resizeSub?.unsubscribe();
    this.buildingScreenshotSub?.unsubscribe();

    this.layerService.clearLayers();

    if (this.viewer) {
      this.destroyCesiumViewer(this.viewer);
      this.viewer = null;
    }
  }

  initMap(): Promise<any> {
    return new Promise(async (resolve) => {
      this.background = getDefaultBackgroundAliasForZone(this.plan.type_zone);
      const backgroundProvider = BACKGROUND_PROVIDERS.find(a => a.alias === this.background);

      const map = new Cesium.Viewer(this.elementRef.nativeElement, {
        imageryProvider: backgroundProvider.value,
        terrainProvider: await Cesium.createWorldTerrainAsync(),
        timeline : false,
        animation : false,
        sceneMode: Cesium.SceneMode.SCENE3D,
        infoBox: false,
        selectionIndicator: false,
      });

      // Active le test de profondeur pour que les objets derrière le terrain disparaissent
      // Par exemple les batiments..
      map.scene.globe.depthTestAgainstTerrain = true;

      map.scene.requestRenderMode = true;
      map.scene.maximumRenderTimeChange = Infinity;

      map.canvasMode = this.canvasMode;
      map.hasBeenCentered = false;

      this.toggle3DControls(map, this.canvasMode === '3d');

      resolve(map);
    });
  }

  zoomOnSnapshot(snap): void {
    if (!snap?.bbox) {
      return;
    }

    const view = JSON.parse(snap.bbox);

    switch (snap.id_zoom) {
      case 'ZOOM_1':
      case 'ZOOM_2':
        // Projection 3D -> CAMERA
        this.viewer.camera.setView(view);

        break;
      default:
        // Projection 2D -> BBOX

        // Bug fix; certaines vielles photos ont été sauvegardées avec des coordonnées en radian
        const bbox = normalizeBBoxToDegrees(view);

        const { west, east, north, south } = bbox;

        this.viewer.camera.flyTo({
          destination: Cesium.Rectangle.fromDegrees(west, south, east, north),
          duration: 0,
        });
        break;
    }

    this.viewer.hasBeenCentered = true;

    this.loadMoveEndLayers();
  }

  private loadMoveEndLayers(): void {
    this.layerService.getActiveLayers().forEach(layer => {
      if (layer.loadType === LayerLoadType.MoveEnd) {
        layer.loadData();
      }
    });
  }

  toggle3DControls(viewer, enabled): void {
    const controller = viewer.scene.screenSpaceCameraController;

    controller.enableTilt = enabled;        // Inclinaison de la caméra
    controller.enableLook = enabled;        // Déplacement de la caméra autour de son propre axe
  }

  cameraMoveEndSub: Subscription;
  initLayer(viewer): void {
    this.viewer = viewer; // Generic alias
    this.viewer.plan = this.plan;
    this.viewer.snapshot = this.snapshot;
    this.viewer.type_risque = this.typeRisque;

    this.viewer.dataSources.dataSourceAdded.addEventListener((_collection, dataSource) => {
      dataSource.entities.collectionChanged.addEventListener(() => {
        this.viewer.scene.requestRender();

        // Un Label ne devient dessinable qu'à la frame suivant sa création (rasterisation du
        // texte dans l'atlas de textures) : le requestRender() ci-dessus peut donc rendre une
        // frame trop tôt. On en redemande un second une fois ce délai passé.
        requestAnimationFrame(() => this.viewer.scene.requestRender());
      });
    });

    this.layerService.initialize(this.viewer);
    this.cesiumEventService.initialize(this.viewer);

    this.cameraMoveEndSub = this.cesiumEventService.cameraMoveEnd$.subscribe(() => {
      this.ngZone.run(() => this.loadMoveEndLayers());
    });
  }

  ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => {
      this.initMap().then((viewer) => {
        if (this.destroyed) {
          this.destroyCesiumViewer(viewer);

          return;
        }

        this.initLayer(viewer);

        this.iconSize = getIconSize(viewer);

        this.viewer.scene.globe.maximumScreenSpaceError = 1;

        this.viewer.scene.globe.tileLoadProgressEvent.addEventListener((tilesCount) => {
          if (this.tilesToLoad < tilesCount) {
            this.tilesToLoad = tilesCount;
            this.ngZone.run(() => this.isMapLoadingTiles.next(true));
          }

          if (tilesCount === 0) {
            this.tilesToLoad = 0;
            this.ngZone.run(() => this.isMapLoadingTiles.next(false));
          }

        });

        this.ngZone.run(() => this.whenMapIsInit.next(true));
      });
    });
  }

  printWidget = (e): any => {
      this.updateNorthArrow();
      this.updateDistanceLegendCesium({
        barWidth: 0,
        distanceLabel: '',
        enableDistanceLegend: true
      }, this.viewer.scene)
  }

  ngOnInit(): void {
    this.buildingScreenshotSub = this.isBuildingScreenShot.subscribe((v) => this.buildingScreenshot = v);

    this.whenMapIsInit.subscribe(() => {
      this.ngZone.runOutsideAngular(() => {
        this.viewer.scene.postRender.addEventListener(this.printWidget);
      });

      this.setCanvasDimension(this.canvasMode);
      this.listenWindowResize();
    });
  }

  private listenWindowResize(): void {
    this.resizeSub?.unsubscribe();
    this.resizeSub = fromEvent(window, 'resize')
      .pipe(debounceTime(150))
      .subscribe(() => {
        if (this.destroyed || !this.viewer || this.buildingScreenshot) {
          return;
        }

        this.setCanvasDimension(this.canvasMode);
        this.viewer.render();
      });
  }

  private destroyCesiumViewer(viewer: Viewer): void {
    if (viewer.scene) {
      viewer.scene.postRender.removeEventListener(this.printWidget)
    }

    if (viewer.screenSpaceEventHandler) {
      viewer.screenSpaceEventHandler.destroy();
    }

    if (viewer.dataSources) {
      viewer.dataSources.removeAll();
    }

    if (viewer.entities) {
      viewer.entities.removeAll();
    }
    if (viewer.scene && viewer.scene.primitives) {
      viewer.scene.primitives.removeAll();
    }

    viewer.destroy();
  }

  updateNorthArrow(): any {
    const angle = this.viewer.camera.heading;
    const degree = angle * Cesium.Math.DEGREES_PER_RADIAN;

    // Ecriture DOM directe (pas de binding Angular) : ce code tourne à chaque frame en dehors
    // de la zone Angular, un binding [style.transform] forcerait un cycle de détection de
    // changement par frame.
    this.northArrow.nativeElement.style.transform = `rotate(${-degree}deg)`;
  }

  updateDistanceLegendCesium(viewModel, scene) {
    if (!viewModel.enableDistanceLegend)
    {
      viewModel.barWidth = undefined;
      viewModel.distanceLabel = undefined;
      return;
    }
    const now = new Date().getTime();
    if (now < viewModel._lastLegendUpdate + 25000) {
      return;
    }

    viewModel._lastLegendUpdate = now;

    // Find the distance between two pixels at the bottom center of the screen.
    const width = scene.canvas.clientWidth;
    const height = scene.canvas.clientHeight;

    const left = scene.camera.getPickRay(new Cesium.Cartesian2((width / 2) | 0, height - 1));
    const right = scene.camera.getPickRay(new Cesium.Cartesian2(1 + (width / 2) | 0, height - 1));

    const globe = scene.globe;
    const leftPosition = globe.pick(left, scene);
    const rightPosition = globe.pick(right, scene);

    if (!Cesium.defined(leftPosition) || !Cesium.defined(rightPosition)) {
      viewModel.barWidth = undefined;
      viewModel.distanceLabel = undefined;
      return;
    }

    const leftCartographic = globe.ellipsoid.cartesianToCartographic(leftPosition);
    const rightCartographic = globe.ellipsoid.cartesianToCartographic(rightPosition);

    this.geodesic.setEndPoints(leftCartographic, rightCartographic);
    const pixelDistance = this.geodesic.surfaceDistance;

    // Find the first distance that makes the scale bar less than 100 pixels.
    const maxBarWidth = 100;
    let distance;
    for (var i = this.distances.length - 1; !Cesium.defined(distance) && i >= 0; --i) {
      if (this.distances[i] / pixelDistance < maxBarWidth) {
        distance = this.distances[i];
      }
    }

    if (Cesium.defined(distance)) {
      let label;
      if (distance >= 1000) {
        label = (distance / 1000).toString() + ' km';
      } else {
        label = distance.toString() + ' m';
      }

      viewModel.barWidth = (distance / pixelDistance) | 0;
      viewModel.distanceLabel = label;

      this.updateEchelleView(viewModel);
    } else {
      viewModel.barWidth = undefined;
      viewModel.distanceLabel = undefined;
    }
  }

  updateEchelleView(viewModel) {
    this.echelle = viewModel;

    if (this.echelleLabel.nativeElement.innerText !== viewModel.distanceLabel) {
      this.echelleLabel.nativeElement.innerText = viewModel.distanceLabel;
    }

    if (this.echelleBar.nativeElement.style.width !== viewModel.barWidth + 'px') {
      this.echelleBar.nativeElement.style.width = viewModel.barWidth + 'px';
    }
  }


  build2dScreenshot = () => {
    this.isBuildingScreenShot.next(true);
    const rectangleCords = getCameraBoundingBox(this.viewer);
    const bbox = [rectangleCords.west, rectangleCords.south, rectangleCords.east, rectangleCords.north];

    // @ts-ignore
    const containerW = this.viewer.container.offsetWidth;
    const icon = Math.round(((5000 * 1.357) / containerW) * (getIconSize(this.viewer) * 2.6));

    this.processMap(bbox, icon);
  }

  take3dScreenshot = (snapshot) => {
    this.isBuildingScreenShot.next(true);
    this.isLoadingLayer.next(true);

    const rectangleCords = getCameraBoundingBox(this.viewer);
    const bbox = [rectangleCords.west, rectangleCords.south, rectangleCords.east, rectangleCords.north];

    this.visiblePictosOnMap = [];

    this.layerService.getActiveLayers().forEach((layer: LayerDatasource | LayerImagery) => {
      // some layer may need to be modified before print
      if (layer.onBeforePrint) {
        layer.onBeforePrint();
      }

      if (layer instanceof PlanLayer) {
        this.visiblePictosOnMap = getVisiblesPlanMarkersFromCurrentViewbox(layer, bbox);
      }
    });

    const diffBetweenScreenAndScreenshotSize = (1500 * 100 / this.canvasWidth);

    const echelleFixedValue = {
      ...this.echelle,
      barWidthComputed: (this.echelle.barWidth * diffBetweenScreenAndScreenshotSize) / 100
    };

    this.setMapCanvasSize(1, 1500);

    this.viewer.render();

    const screenshot = (event?) => {
      if (event) {
        event.unsubscribe();
      }

      this.viewer.scene.requestRender();
      this.viewer.render();
      const baseScreenshot = this.viewer.canvas.toDataURL('image/jpeg', 1);

      ////// SCREENSHOT
      const ctx = this.canvasSnapshot.nativeElement.getContext('2d');
      ctx.clearRect(0, 0, this.canvasSnapshot.nativeElement.width, this.canvasSnapshot.nativeElement.height);
      const img = document.createElement('img');
      img.src = baseScreenshot;
      img.addEventListener('load', () => {
        /**
         * The actual map screenshot
         */
        ctx.drawImage(img, 0, 0);

        /**
         * The scale
         */
        const originLeft = 1500 - (echelleFixedValue.barWidthComputed + 150);

        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
        ctx.shadowBlur = 0;

        // background
        ctx.beginPath();
        ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
        ctx.rect(originLeft, 1500 - 50, echelleFixedValue.barWidthComputed + 150, 50);
        ctx.fill();
        ctx.closePath();

        // label
        ctx.fillStyle = 'black';
        ctx.textAlign = 'start';
        ctx.font = '30px Arial';
        ctx.fillText(echelleFixedValue.distanceLabel, originLeft + echelleFixedValue.barWidthComputed + 50, 1500 - 15);

        //// BAR@
        ctx.beginPath();
        ctx.fillStyle = 'black';
        //left
        ctx.rect(originLeft + 30, 1500 - 29, 3, 15);
        ctx.fill();
        //bottom
        ctx.rect(originLeft + 30, 1500 - 15, echelleFixedValue.barWidthComputed, 3);
        ctx.fill();
        //right
        ctx.rect(originLeft + 27 + echelleFixedValue.barWidthComputed, 1500 - 30, 3, 15);
        ctx.fill();
        ctx.closePath();


        /**
         * The arrow
         */
        const arrow = document.createElement('img');
        arrow.src = '../../assets/images/north-arrow.png';
        arrow.addEventListener('load', () => {
          const imgSize = 100;

          ctx.save();
          ctx.translate(1400 + imgSize / 2,  imgSize / 2);
          ctx.rotate(-this.viewer.camera.heading);
          ctx.drawImage(arrow, imgSize / 2 * (-1), imgSize / 2 * (-1), imgSize, imgSize);
          ctx.restore();


          const finalScreenshot = this.canvasSnapshot.nativeElement.toDataURL('image/jpeg', 1);
          const blob = this.downloadService.dataURItoBlob(finalScreenshot);


          this.downloadService.uploadSnapshot(blob, this.plan.id, snapshot.type).subscribe((res) => {
            this.upload = res;

            if (res.state === 'DONE' && !!res.content.url) {
              this.upload = null;

              this.mapperService.createOrUpdateSnapshot(this.plan, {
                ...snapshot,
                type: snapshot.type,
                pictos: this.visiblePictosOnMap,
                snapshot_url_image: res.content.url,
                position: INITIAL_PRINT_POSITION[snapshot.type],
                bbox: getCamera(this.viewer)
              }).subscribe(() => {
                this.setMapCanvasSize(1, document.body.clientHeight * 0.80);
                this.viewer.render();

                this.layerService.getActiveLayers().forEach((layer: LayerDatasource | LayerImagery) => {
                  // some layer may need to be modified before print
                  if (layer.onAfterPrint) {
                    layer.onAfterPrint();
                  }
                });

                this.snackbar.open('Le zoom a été sauvegardé', 'ok', {
                  duration: 3000
                });

                this.isLoadingLayer.next(false);
                this.isBuildingScreenShot.next(false);

                this.viewer.render();
              }, () => {
                this.setMapCanvasSize(1, document.body.clientHeight * 0.80);

                this.isLoadingLayer.next(false);
                this.isBuildingScreenShot.next(false);

                this.viewer.render();
                this.feedback('danger', `Un problème est survenue pendant l'envoies du fichier`);
              });
            }
          }, (err) => {
            this.setMapCanvasSize(1, document.body.clientHeight * 0.80);

            this.isLoadingLayer.next(false);
            this.isBuildingScreenShot.next(false);

            this.viewer.render();
            this.feedback('danger', `Un problème est survenue pendant l'envoies du fichier`);
          });
        });
      });

      this.layerService.getActiveLayers().forEach((layer: LayerDatasource | LayerImagery) => {
        if (layer.onAfterPrint) {
          layer.onAfterPrint();
        }
      });
    };

    if (this.viewer.scene.globe.tilesLoaded) {
      setTimeout(() => {
        // pour éviter de capturer l'image si tout les tiles sont déjà charger avant que le
        // layers pcs ne soit refresh
        screenshot();
      }, 1000);
    } else {
      const subscription = this.isMapLoadingTiles.subscribe((isLoading) => {
        if (!isLoading) {
          screenshot(subscription);
        }
      });
    }
  }

  feedback(type, msg): void {
    this.snackbar.open(msg, null, {
      duration: 3000
    });
  }

  handleNiveauLayer(listLayer: Array<any>): Array<string> {
    return listLayer.filter((layer) => layer.detailVisible).map((layer) => `${layer.id}_niveau`);
  }

  processMap(bbox, icon): void {

    const listActiveLayer = this.layerService.getActiveLayers();
    const listActiveLayerIds = listActiveLayer.map((a) => a.id);

    const showNiveauLayer = this.handleNiveauLayer(listActiveLayer);

    const diffBetweenScreenAndScreenshotSize = (6785 / this.canvasWidth);
    const hideLabelFromLayerByIds = [];

    let watercourse = null;
    let watercourseCiv = null;
    this.visiblePictosOnMap = [];

    listActiveLayer.forEach((layer: LayerDatasource) => {
      if (layer instanceof PlanLayer) {
        this.visiblePictosOnMap = getVisiblesPlanMarkersFromCurrentViewbox(layer, bbox);
      }

      if (layer.hasOwnProperty('labelVisible')) {
        if (!layer.labelVisible) {
          hideLabelFromLayerByIds.push(layer.id);
          return;
        }

        if (layer instanceof WatercourseLayer) {
          watercourse = {
            distanceBetweenLabels: layer.labelDistance
          };
        }

        if (layer instanceof WatercourseCIVLayer) {
          watercourseCiv = {
            distanceBetweenLabels: layer.labelDistance
          };
        }
      }
    });

    const nameFile = this.plan.code_zone
      ? this.plan.code_zone + '-map-2d.jpg'
      : this.plan.title + '-map-2d.jpg';

    const options = {
      icon, bbox,
      width: 6785,
      height: 5000,
      background: this.background,
      echelle: {
        label: this.echelle.distanceLabel,
        barWidth: (this.echelle.barWidth * diffBetweenScreenAndScreenshotSize),
      },
      hide_label_from_layers: hideLabelFromLayerByIds,
      watercourse,
      watercourse_civ: watercourseCiv,
      code_zone: this.plan.code_zone,
      type_zone: this.plan.type_zone,
      list_layer_to_show: listActiveLayerIds,
      show_niveau_layer: showNiveauLayer,
      namefile: nameFile,
      pcs_layer: this.visiblePictosOnMap,
      id_user: this.plan.id_user || null,
      type_risque: this.typeRisque,
      uuid_a0: this.plan.id
    };

    this.socketService.processMap(options);
  }

  setCanvasDimension(mode: string): any {
    if (mode === '2d') {
      this.setMapCanvasSize(1.357, document.body.clientHeight * 0.80);
    } else {
      this.setMapCanvasSize(1, document.body.clientHeight * 0.80);
    }
  }

  setMapCanvasSize(ratio, size): void {
    this.canvasSize(size * ratio, size, ratio);
  }

  canvasSize(width, height, ratio): any {
    this.canvasWidth = width;
    this.canvasHeight = height;

    this.viewer.canvasWidth = width;


    // @ts-ignore
    this.viewer.container.style.height = height + 'px';
    // @ts-ignore
    this.viewer.container.style.width = width + 'px';
    // @ts-ignore
    this.viewer.container.style.transform = `scale(${document.body.clientHeight * 0.80 / height})`;
  }
}
